import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Menu,
  User,
  ChevronDown,
  LogOut,
  Home,
  Store,
  BookOpen,
  Bell,
  Settings,
  Users,
  BarChart2,
  ClipboardList,
  Award,
  DollarSign,
  Headphones,
  Package,
  FileText
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
// Using SVG logo as an image instead of component
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { getAriaLabel, keyboardHandlers } from '../../utils/accessibility';
import LanguageSwitcher from './LanguageSwitcher';
import { NotificationBell } from '../notifications/NotificationBell';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Breadcrumb } from './Breadcrumb';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface HeaderProps {
  onMenuToggle: () => void;
  isMenuCollapsed: boolean;
  notificationCount?: number;
}

const HeaderContainer = styled.header<{ isMenuCollapsed: boolean }>`
  position: fixed;
  top: 0;
  left: ${props => props.isMenuCollapsed ? '80px' : '280px'};
  right: 0;
  height: 80px;
  background-color: ${ThemeColors.background.header};
  border-bottom: 1px solid ${ThemeColors.border.light};
  box-shadow: ${Shadows.sm};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${Spacing.xl};
  z-index: 999;
  transition: left 0.3s ease;
  
  @media (max-width: 768px) {
    left: 0;
    padding: 0 ${Spacing.md};
    height: 64px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
`;

const MenuButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${Spacing.sm};
  color: ${ThemeColors.text.primary};
  border-radius: ${BorderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${ThemeColors.gray[100]};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${ThemeColors.focus.ring};
  }
`;

const LogoSection = styled.div<{ isMenuCollapsed: boolean }>`
  display: flex;
  align-items: center;
  
  @media (min-width: 769px) {
    display: ${props => props.isMenuCollapsed ? 'flex' : 'none'};
  }
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
  margin-left: ${Spacing.xs};
  
  @media (max-width: 768px) {
    height: 32px;
  }
`;

const BreadcrumbWrapper = styled.div`
  margin-left: ${Spacing.lg};
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  position: relative;
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${Spacing.sm};
  position: relative;
  color: ${ThemeColors.text.secondary};
  border-radius: ${BorderRadius.round};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${ThemeColors.gray[100]};
    color: ${ThemeColors.text.primary};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${ThemeColors.focus.ring};
  }
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: ${ThemeColors.error.main};
  border-radius: ${BorderRadius.round};
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  color: white;
  border: 2px solid ${ThemeColors.background.header};
`;

const Divider = styled.div`
  width: 1px;
  height: 32px;
  background-color: ${ThemeColors.border.light};
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserSectionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  cursor: pointer;
  padding: ${Spacing.sm} ${Spacing.md};
  border-radius: ${BorderRadius.md};
  transition: all 0.2s ease;
  border: none;
  background: transparent;
  
  &:hover,
  &:focus {
    background-color: ${ThemeColors.gray[100]};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${ThemeColors.focus.ring};
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ChevronIcon = styled(ChevronDown).withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  color: ${ThemeColors.text.secondary};
  transition: transform 0.2s ease;
  transform: rotate(${props => (props.isOpen ? '180deg' : '0deg')});
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${BorderRadius.round};
  background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${Typography.fontWeight.bold};
  font-size: ${Typography.fontSize.md};
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: ${Typography.fontSize.sm};
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
`;

const UserRole = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
`;

const UserMenuContainer = styled.div`
  position: absolute;
  top: 72px;
  right: 0;
  background: ${ThemeColors.background.paper};
  border-radius: ${BorderRadius.md};
  box-shadow: ${Shadows.dropdown};
  border: 1px solid ${ThemeColors.border.light};
  min-width: 220px;
  padding: ${Spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  z-index: 1000;
`;

const UserMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border-radius: ${BorderRadius.md};
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: ${Typography.fontFamily.primary};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  transition: background 0.2s ease;

  &:hover,
  &:focus {
    background-color: ${ThemeColors.primary.light}33;
    outline: none;
  }
`;

export const Header: React.FC<HeaderProps> = ({ 
  onMenuToggle, 
  isMenuCollapsed, 
  notificationCount = 0 
}) => {
  const { t } = useTranslation();
  const { user, userRole, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [storeBreadcrumbLabels, setStoreBreadcrumbLabels] = useState<Record<string, string>>({});
  const menuRef = useRef<HTMLDivElement | null>(null);
  const profileButtonRef = useRef<HTMLButtonElement | null>(null);

  const ensureStoreLabel = useCallback(async (storeId: string, providedLabel?: string) => {
    if (!storeId) {
      return;
    }

    if (storeBreadcrumbLabels[storeId]) {
      return;
    }

    if (providedLabel) {
      setStoreBreadcrumbLabels(prev => ({
        ...prev,
        [storeId]: String(providedLabel)
      }));
      return;
    }

    try {
      const storeRef = doc(db, 'Stores', storeId);
      const storeSnap = await getDoc(storeRef);

      if (storeSnap.exists()) {
        const data = storeSnap.data() as Record<string, unknown>;
        const label =
          data?.CustomerNo ??
          data?.customerNo ??
          data?.customer_number ??
          data?.customerNumber ??
          data?.StoreNumber ??
          data?.storeNumber ??
          data?.StoreName ??
          data?.storeName ??
          data?.name ??
          storeId;

        setStoreBreadcrumbLabels(prev => ({
          ...prev,
          [storeId]: String(label)
        }));
      }
    } catch (error) {
      console.error('Failed to load store breadcrumb label', error);
    }
  }, [storeBreadcrumbLabels]);

  useEffect(() => {
    const state = location.state as {
      storeId?: string;
      storeBreadcrumbLabel?: string;
      customerNumber?: string;
    } | null;

    if (state?.storeId) {
      const labelFromState = state.customerNumber ?? state.storeBreadcrumbLabel;
      if (labelFromState) {
        ensureStoreLabel(state.storeId, labelFromState);
      }
    }
  }, [location.state, ensureStoreLabel]);

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const storeDetailsIndex = segments.findIndex(segment => segment === 'store-details');

    if (storeDetailsIndex !== -1) {
      const storeId = segments[storeDetailsIndex + 1];
      if (storeId) {
        ensureStoreLabel(storeId);
      }
    }
  }, [location.pathname, ensureStoreLabel]);

  const userInitials = useMemo(() => {
    if (!user?.username && !user?.email) return 'SR';
    const source = user?.username || user?.email || '';
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    if (parts.length === 0) {
      return source.slice(0, 2).toUpperCase();
    }
    return parts
      .map(part => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }, [user?.username, user?.email]);

  const userDisplayName = useMemo(() => {
    if (user?.username) return user.username;
    if (user?.email) return user.email.split('@')[0];
    return t('common.anonymousUser', 'Guest User');
  }, [user?.username, user?.email, t]);

  const previewTail = searchParams.get('preview');

  const breadcrumbItems = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const isManagerPath = segments[0] === 'manager';
    const baseDashboardPath = isManagerPath ? '/manager/dashboard' : '/dashboard';

    const segmentConfig: Record<
      string,
      {
        label: string;
        icon?: React.ReactNode;
        resolvePath?: (info: { isManager: boolean; pathAccumulator: string }) => string | undefined;
      }
    > = {
      'store-visit': { label: t('navigation.storeVisit', 'Store Visit'), icon: <Store size={14} /> },
      stores: { label: t('navigation.storeVisit', 'Store Visit'), icon: <Store size={14} /> },
      'store-details': { 
        label: t('navigation.storeVisit', 'Store Visit'), 
        icon: <Store size={14} />,
        resolvePath: ({ isManager }) => (isManager ? '/manager/stores' : '/store-visit')
      },
      resources: { label: t('resources.title', 'Resource Library'), icon: <BookOpen size={14} /> },
      'store-resource': { label: t('navigation.storeResource', 'Store Resource'), icon: <BookOpen size={14} /> },
      announcements: { label: t('navigation.announcements', 'Announcements'), icon: <Bell size={14} /> },
      settings: { label: t('navigation.settings', 'Settings'), icon: <Settings size={14} /> },
      'user-management': { label: t('navigation.userManagement', 'User Management'), icon: <Users size={14} /> },
      'order-management': { label: t('navigation.orderManagement', 'Order Management'), icon: <ClipboardList size={14} /> },
      orders: {
        label: t('navigation.orders', 'Orders'),
        icon: <ClipboardList size={14} />,
        resolvePath: ({ isManager }) => (isManager ? '/manager/orders' : '/orders')
      },
      'order-details': {
        label: t('navigation.orderDetails', 'Order Details'),
        icon: <ClipboardList size={14} />,
        resolvePath: ({ isManager }) => (isManager ? '/manager/orders' : '/orders')
      },
      analytics: { label: t('navigation.analytics', 'Analytics'), icon: <BarChart2 size={14} /> },
      payroll: { label: t('navigation.payroll', 'Payroll'), icon: <DollarSign size={14} /> },
      profile: { label: t('common.profile', 'Profile'), icon: <User size={14} /> },
      'request-contact': { label: t('navigation.requestContact', 'Request Contact'), icon: <Headphones size={14} /> },
      'order-supplies': { label: t('navigation.orderSupplies', 'Order Supplies'), icon: <Package size={14} /> },
      'contest-entry': { label: t('navigation.contestEntry', 'Contest Entry'), icon: <Award size={14} /> },
      products: { label: 'Product Management', icon: <Package size={14} /> },
      'eos-survey': { label: 'EOS Survey', icon: <ClipboardList size={14} /> },
      tradeshow: { label: 'Trade Show Orders', icon: <ClipboardList size={14} /> },
      shipments: { label: 'Shipments', icon: <Package size={14} /> },
      users: { label: t('navigation.userManagement', 'User Management'), icon: <Users size={14} /> },
    };

    const items: Array<{ label: string; icon?: React.ReactNode; path?: string }> = [
      {
        label: t('navigation.dashboard', 'Dashboard'),
        icon: <Home size={14} />,
        path: baseDashboardPath
      }
    ];

    let pathAccumulator = isManagerPath ? '/manager' : '';

    segments.forEach((segment, index) => {
      if (segment === 'manager' || segment === 'dashboard') {
        if (segment === 'manager') {
          pathAccumulator = '/manager';
        }
        return;
      }

      pathAccumulator += `/${segment}`;

      const config = segmentConfig[segment];
      let label = config?.label;
      let icon = config?.icon;
      let path = config?.resolvePath
        ? config.resolvePath({ isManager: isManagerPath, pathAccumulator })
        : pathAccumulator;

      if (!label) {
        if (/^[a-zA-Z0-9_-]{8,}$/.test(segment)) {
          const previousSegment = segments[index - 1];

          if (previousSegment === 'order-details') {
            label = `${t('navigation.order', 'Order')} ${segment}`;
            path = undefined;
          } else if (previousSegment === 'store-details' || previousSegment === 'stores') {
            const storeLabel = storeBreadcrumbLabels[segment];
            label = storeLabel ? String(storeLabel) : segment;
            icon = <Store size={14} />;
            path = isManagerPath ? `/manager/store-details/${segment}` : `/store-details/${segment}`;
          } else if (previousSegment === 'resources') {
            label = `${t('resources.resource', 'Resource')} ${segment}`;
            path = undefined;
          } else {
            label = segment.toUpperCase();
            path = undefined;
          }
        } else {
          label = segment
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }
      }

      items.push({
        label,
        icon,
        path
      });
    });

    const onStoreDetailsRoute = location.pathname.includes('/store-details/');
    if (
      onStoreDetailsRoute &&
      (previewTail === 'order' || previewTail === 'supplies')
    ) {
      const label =
        previewTail === 'supplies'
          ? `${t('orderPreview.title')} — ${t('categories.supply', 'Supplies')}`
          : t('orderPreview.title');
      items.push({
        label,
        icon: <FileText size={14} />,
        path: undefined,
      });
    }

    return items.map((item, index) => ({
      label: item.label,
      icon: item.icon,
      isActive: index === items.length - 1,
      onClick: index === items.length - 1 ? undefined : () => navigate(item.path || baseDashboardPath)
    }));
  }, [location.pathname, navigate, t, storeBreadcrumbLabels, previewTail]);

  const closeMenu = useCallback(() => setIsProfileMenuOpen(false), []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isProfileMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen, closeMenu]);

  const handleProfileToggle = useCallback(() => {
    setIsProfileMenuOpen(prev => !prev);
  }, []);

  const handleViewProfile = useCallback(() => {
    const hasManagerAccess = userRole === 'manager' || userRole === 'hand-lead' || userRole === 'admin';
    if (hasManagerAccess) {
      navigate('/manager/profile');
    } else {
      navigate('/profile');
    }
    closeMenu();
  }, [navigate, closeMenu, userRole]);

  const handleLogout = useCallback(async () => {
    try {
      closeMenu();
      await signOut();
      navigate('/login');
    } catch (error: any) {
      console.error('Logout failed:', error);
      toast.error(error?.message || t('auth.logoutFailed', 'Failed to log out. Please try again.'));
    }
  }, [signOut, navigate, closeMenu, t]);

  return (
    <HeaderContainer isMenuCollapsed={isMenuCollapsed}>
      <LeftSection>
        <MenuButton 
          onClick={onMenuToggle} 
          aria-label={getAriaLabel('Toggle', 'menu', isMenuCollapsed ? 'collapsed' : 'expanded')}
          aria-expanded={!isMenuCollapsed}
          aria-controls="main-navigation"
        >
          <Menu size={24} aria-hidden="true" />
        </MenuButton>
        <LogoSection isMenuCollapsed={isMenuCollapsed}>
          <LogoImage src="/assets/erp-logo.svg" alt="ERP Mock Logo" />
        </LogoSection>
        <BreadcrumbWrapper>
          <Breadcrumb items={breadcrumbItems} />
        </BreadcrumbWrapper>
      </LeftSection>

      <RightSection>
        <LanguageSwitcher variant="compact" />
        <NotificationBell />
        <Divider />
        <UserSectionButton
          ref={profileButtonRef}
          type="button"
          role="button" 
          aria-label={getAriaLabel('Toggle', 'user menu')}
          aria-expanded={isProfileMenuOpen}
          aria-haspopup="true"
          isOpen={isProfileMenuOpen}
          onClick={handleProfileToggle}
          onKeyDown={keyboardHandlers.onActivate(handleProfileToggle)}
        >
          <UserAvatar aria-hidden="true">
            {userInitials}
          </UserAvatar>
          <UserInfo>
            <UserName>{t('common.profile', 'Profile')}</UserName>
            <UserRole>{userDisplayName}</UserRole>
          </UserInfo>
          <ChevronIcon size={16} aria-hidden="true" isOpen={isProfileMenuOpen} />
        </UserSectionButton>
        {isProfileMenuOpen && (
          <UserMenuContainer ref={menuRef}>
            <UserMenuItem onClick={handleViewProfile}>
              <User size={18} />
              {t('header.profileMenu.viewProfile', 'View Profile')}
            </UserMenuItem>
            <UserMenuItem onClick={handleLogout}>
              <LogOut size={18} />
              {t('header.profileMenu.logout', 'Logout')}
            </UserMenuItem>
          </UserMenuContainer>
        )}
      </RightSection>
    </HeaderContainer>
  );
};